import { useSortable } from '@dnd-kit/sortable';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import Box from '@mui/material/Box';
import { useTheme } from '@mui/material/styles';
import TableCell from '@mui/material/TableCell';
import React from 'react';

import { ColumnDef, SortModel } from './ServiceTable.types';
import { ColumnResizeHandle } from '../../atoms/ColumnResizeHandle';

export interface SortableHeaderCellProps {
  col: ColumnDef;
  sortModel: SortModel;
  onSort: (field: string) => void;
  dropIndicator?: 'left' | 'right' | null;
  /** When provided, renders a drag-to-resize handle on the column's right edge
   *  that commits new widths via this callback. */
  onColumnResize?: (key: string, width: number, seed?: Record<string, number>) => void;
}

const HEADER_SX = {
  fontSize: '10px',
  textTransform: 'uppercase' as const,
  letterSpacing: '0.5px',
  fontWeight: 600,
  color: 'text.secondary',
  userSelect: 'none' as const,
  whiteSpace: 'nowrap' as const,
  py: 0.75,
  px: 1,
  borderBottom: '2px solid',
  borderColor: 'v4.border',
} as const;

const dropIndicatorSx = (side: 'left' | 'right' | null | undefined, accent: string) => {
  if (side !== 'left' && side !== 'right') return undefined;
  const pseudo = side === 'left' ? '&::before' : '&::after';
  return {
    [pseudo]: {
      content: '""',
      position: 'absolute',
      [side]: 0,
      top: 4,
      bottom: 4,
      width: '2px',
      bgcolor: accent,
      borderRadius: '1px',
    },
  };
};

const widthSx = (col: ColumnDef) => {
  if (col.width !== undefined) return { minWidth: col.width, maxWidth: col.width };
  const sx: { minWidth?: number; maxWidth?: number } = {};
  if (col.minWidth !== undefined) sx.minWidth = col.minWidth;
  if (col.maxWidth !== undefined) sx.maxWidth = col.maxWidth;
  return sx;
};

export const SortableHeaderCell: React.FC<SortableHeaderCellProps> = ({
  col,
  sortModel,
  onSort,
  dropIndicator,
  onColumnResize,
}) => {
  const theme = useTheme();
  const { attributes, listeners, setNodeRef, isDragging } = useSortable({ id: col.key });
  const isSorted = sortModel.field === col.key;

  const ariaSortValue = isSorted
    ? sortModel.direction === 'asc'
      ? 'ascending'
      : 'descending'
    : 'none';

  const hasWidthConstraint =
    col.width !== undefined || col.maxWidth !== undefined || col.minWidth !== undefined;

  return (
    <TableCell
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      aria-sort={ariaSortValue}
      data-col-key={col.key}
      sx={{
        ...HEADER_SX,
        position: 'relative',
        cursor: isDragging ? 'grabbing' : 'grab',
        opacity: isDragging ? 0.4 : 1,
        color: isSorted ? theme.palette.v4.accent : 'text.secondary',
        '& .header-end-adornment': {
          opacity: 0,
          transition: 'opacity 0.15s ease',
        },
        '&:hover .header-end-adornment, &:focus-within .header-end-adornment': {
          opacity: 1,
        },
        '&:hover .col-resize-handle, &:focus-within .col-resize-handle': {
          opacity: 1,
        },
        ...widthSx(col),
        ...(hasWidthConstraint ? { overflow: 'hidden' } : {}),
        ...dropIndicatorSx(dropIndicator, theme.palette.v4.accent),
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          maxWidth: '100%',
          minWidth: 0,
        }}
      >
        <Box
          component="button"
          type="button"
          onClick={() => onSort(col.key)}
          aria-label={`Sort by ${col.label}, ${ariaSortValue}`}
          title={col.label}
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.5,
            maxWidth: '100%',
            minWidth: 0,
            overflow: 'hidden',
            background: 'none',
            border: 'none',
            padding: 0,
            font: 'inherit',
            color: 'inherit',
            cursor: 'pointer',
            letterSpacing: 'inherit',
            textTransform: 'inherit',
            fontWeight: 'inherit',
          }}
        >
          <Box
            component="span"
            sx={{
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {col.headerLabel ?? col.label}
          </Box>
          {isSorted &&
            (sortModel.direction === 'asc' ? (
              <ArrowUpwardIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            ) : (
              <ArrowDownwardIcon sx={{ fontSize: 14, flexShrink: 0 }} />
            ))}
        </Box>
        {col.headerEndAdornment ? (
          <Box
            className="header-end-adornment"
            sx={{ display: 'inline-flex', alignItems: 'center', flexShrink: 0 }}
          >
            {col.headerEndAdornment}
          </Box>
        ) : null}
      </Box>
      {onColumnResize && (
        <ColumnResizeHandle columnKey={col.key} label={col.label} onResize={onColumnResize} />
      )}
    </TableCell>
  );
};
